import React, { useState, useRef, useEffect } from "react";
import axios from "axios";
import { BASE_URL, COMPARE_URL } from "../../common/constants";
import Papa from "papaparse";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

const CompareFiles = () => {
  const [parties, setParties] = useState([]);
  const [selectedParty, setSelectedParty] = useState("");
  const [loadingParties, setLoadingParties] = useState(false);
  const [partySearchTerm, setPartySearchTerm] = useState("");
  const [showPartyDropdown, setShowPartyDropdown] = useState(false);
  const [stockistFile, setStockistFile] = useState([]);
  const [cfaFile, setCfaFile] = useState(null);
  const [comparisonResult, setComparisonResult] = useState([]);
  const [skuMap, setSkuMap] = useState({});
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [downloadOption, setDownloadOption] = useState("all");
  const [editRowKey, setEditRowKey] = useState(null);
  const [editedRow, setEditedRow] = useState({});

  const stockistRef = useRef(null);
  const cfaRef = useRef(null);
  const editInputRef = useRef(null);

  const compareUrl = `${COMPARE_URL}/compare_files`;
  const token = sessionStorage.getItem("token");
  const pharma_company_id = "fdc";
  const navigate = useNavigate();

  useEffect(() => {
    sessionStorage.setItem("partyCode", "");
    sessionStorage.setItem("partyName", "");
    setSelectedParty("");
  }, []);

  useEffect(() => {
    if (editInputRef.current) {
      editInputRef.current.focus({ preventScroll: true });
    }
  }, [editRowKey]);

  useEffect(() => {
    if (!token) navigate("/");
  }, [token, navigate]);

  useEffect(() => {
    const fetchParties = async () => {
      setLoadingParties(true);
      try {
        const response = await axios.get(`${BASE_URL}/user/associations`, { headers: { token } });
        let partiesData = [];
        if (Array.isArray(response.data)) partiesData = response.data;
        else if (response.data && Array.isArray(response.data.data)) partiesData = response.data.data;
        else if (response.data && Array.isArray(response.data.associations)) partiesData = response.data.associations;
        else if (response.data && typeof response.data === "object") partiesData = Object.values(response.data);

        setParties(partiesData);
      } catch (error) {
        console.error("Error fetching parties:", error);
        toast.error(`Failed to fetch parties. ${error.message || ""}`);
        setParties([]);
      } finally {
        setLoadingParties(false);
      }
    };
    if (token) fetchParties();
  }, [token]);

  const filteredParties = parties.filter(
    (party) =>
      String(party.name ?? "").toLowerCase().includes(partySearchTerm.toLowerCase()) ||
      String(party.code ?? "").toLowerCase().includes(partySearchTerm.toLowerCase()) ||
      String(party.address ?? "").toLowerCase().includes(partySearchTerm.toLowerCase())
  );

  const selectedPartyDetails = parties.find(
    (party) => party._id === selectedParty || party.code === selectedParty
  );

  const handlePartySelect = (party) => {
    setSelectedParty(party._id || party.code);
    setPartySearchTerm("");
    setShowPartyDropdown(false);
    sessionStorage.setItem("partyCode", party.code ?? "");
    sessionStorage.setItem("partyName", party.name ?? "");
  };

  const getFieldValue = (row, fieldNames) => {
    for (let name of fieldNames) {
      if (row[name] !== undefined && row[name] !== null) {
        return row[name];
      }
    }
    return "";
  };

  const normalizeMRP = (mrpValue) => {
    if (mrpValue === null || mrpValue === undefined || mrpValue === "") return "0";
    const cleaned = String(mrpValue).replace(/[₹$,\s]/g, "").trim();
    const parsed = parseFloat(cleaned);
    return isNaN(parsed) ? "0" : parsed.toFixed(2);
  };

  const normalizeDate = (dateValue) => {
    if (!dateValue) return "";
    return String(dateValue).replace(/\s+/g, "").trim().toUpperCase();
  };

  const mergeRows = (data) => {
    const mergedMap = {};
    const batchFieldNames = ["Batch Number", "BatchNumber", "Batch", "batch_number"];
    const expiryFieldNames = ["Expiry", "Expiry Date", "ExpiryDate", "expiry", "expiry_date"];
    const mrpFieldNames = ["MRP", "mrp", "Price"];
    const customerQtyNames = ["Customer Quantity", "CustomerQuantity", "customer_quantity", "Qty", "Quantity"];
    const scannedQtyNames = ["Scanned Quantity", "ScannedQuantity", "scanned_quantity", "Scanned", "scanned"];
    const productNameFields = ["Product Name", "ProductName", "product_name", "Product", "product"];

    data.forEach((row) => {
      const batchNumber = String(getFieldValue(row, batchFieldNames) || "").trim().toUpperCase();
      const expiry = normalizeDate(getFieldValue(row, expiryFieldNames));
      const mrp = normalizeMRP(getFieldValue(row, mrpFieldNames));
      const customerQty = parseFloat(getFieldValue(row, customerQtyNames) || 0);
      const scannedQty = parseFloat(getFieldValue(row, scannedQtyNames) || 0);
      const productName = String(getFieldValue(row, productNameFields) || "");

      const key = `${batchNumber}###${expiry}###${mrp}`;

      if (mergedMap[key]) {
        mergedMap[key]["Customer Quantity"] += customerQty;
        mergedMap[key]["Scanned Quantity"] += scannedQty;
        if (!mergedMap[key]["Product Name"] && productName) {
          mergedMap[key]["Product Name"] = productName;
        }
      } else {
        mergedMap[key] = {
          "Product Name": productName,
          "Batch Number": batchNumber,
          "Expiry": expiry,
          "MRP": parseFloat(mrp),
          "Customer Quantity": customerQty,
          "Scanned Quantity": scannedQty,
        };
      }
    });

    return Object.values(mergedMap).map(row => ({
      ...row,
      "Differences": (parseFloat(row["Scanned Quantity"]) - parseFloat(row["Customer Quantity"]))
    }));
  };

  const handleCompare = async () => {
    if (!selectedParty) return toast.warn("Please select a party.");
    if (!stockistFile.length || !cfaFile) return toast.warn("Please upload both files.");
    if (!token) return toast.error("Authentication token is missing. Please log in again.");

    const partyCode = sessionStorage.getItem("partyCode");
    const partyName = sessionStorage.getItem("partyName");
    if (!partyCode) return toast.warn("Party code is missing. Please select a party again.");

    const formData = new FormData();
    stockistFile.forEach((file) => formData.append("customer_file", file));
    formData.append("allscan_file", cfaFile);

    const queryParams = new URLSearchParams({ stockist_code: partyCode, pharma_company_id, stockist_name: partyName });
    const fullUrl = `${compareUrl}?${queryParams.toString()}`;
    setLoading(true);

    try {
      const res = await axios.post(fullUrl, formData, { headers: { Authorization: `Bearer ${token}` } });
      if (res.data?.error_msg) return toast.error(`Error: ${res.data.error_msg}`);

      const parsedResult = Array.isArray(res.data.data)
        ? res.data.data
        : JSON.parse(res.data.data || "[]");

      const skuLookup = {};
      parsedResult.forEach((row) => {
        const key = `${String(row["Batch Number"] ?? "").trim()}|${String(row["Expiry"] ?? "").trim()}|${parseFloat(String(row["MRP"] ?? 0).toString().replace(/,/g, ""))}`;
        if (row["Product Name"]) skuLookup[key] = row["Product Name"];
      });

      const unmatchedItems = parsedResult.filter((row) => {
        const key = `${String(row["Batch Number"] ?? "").trim()}|${String(row["Expiry"] ?? "").trim()}|${parseFloat(String(row["MRP"] ?? 0).toString().replace(/,/g, ""))}`;
        return !skuLookup[key];
      });

      if (unmatchedItems.length > 0) {
        try {
          const skuRes = await axios.post(`${BASE_URL}/product/skus`, {
            items: unmatchedItems.map(row => ({
              "Batch Number": row["Batch Number"] ?? "",
              Expiry: row["Expiry"] ?? "",
              MRP: row["MRP"] ?? 0,
            }))
          }, {
            headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
          });

          (skuRes.data.data ?? []).forEach((m) => {
            const key = `${String(m["Batch Number"] ?? "").trim()}|${String(m["Expiry"] ?? "").trim()}|${parseFloat(String(m["MRP"] ?? 0).toString().replace(/,/g, ""))}`;
            skuLookup[key] = m.SKU ?? "";
          });
        } catch (skuError) {
          console.error("SKU enrichment failed:", skuError);
          toast.error(`SKU enrichment failed. ${skuError.message ?? ""}`);
        }
      }

      const updatedResult = parsedResult.map((row) => {
        const key = `${String(row["Batch Number"] ?? "").trim()}|${String(row["Expiry"] ?? "").trim()}|${parseFloat(String(row["MRP"] ?? 0).toString().replace(/,/g, ""))}`;
        return {
          "Product Name": skuLookup[key] ?? "",
          "Batch Number": String(row["Batch Number"] ?? ""),
          Expiry: String(row["Expiry"] ?? ""),
          MRP: parseFloat(row["MRP"] ?? 0),
          "Customer Quantity": parseFloat(row["Customer Quantity"] ?? 0),
          "Scanned Quantity": parseFloat(row["Scanned Quantity"] ?? 0),
          Differences: parseFloat(row["Differences"] ?? 0),
        };
      });

      setSkuMap(skuLookup);
      setComparisonResult(updatedResult);
      toast.success("Comparison completed successfully!");
    } catch (error) {
      const errorMsg = error.response?.data?.error_msg ?? error.message ?? "Something went wrong during comparison.";
      toast.error(`Error: ${errorMsg}`);
    } finally {
      setLoading(false);
      stockistRef.current.value = null;
      cfaRef.current.value = null;
      setStockistFile([]);
      setCfaFile(null);
    }
  };

  const handleDownloadCSV = () => {
    if (!Array.isArray(comparisonResult) || !comparisonResult.length) return toast.error("No data to download.");

    let rowsToDownload = comparisonResult.map((row) => {
      const diff = parseFloat(row["Differences"] ?? 0);
      const remark = diff === 0 ? "matched" : diff > 0 ? "excess" : "short";
      return { ...row, Remark: remark };
    });

    if (downloadOption === "unmatched") rowsToDownload = rowsToDownload.filter(r => r.Remark !== "matched");
    if (!rowsToDownload.length) return toast.warn("No rows to download based on selected option.");

    const csv = Papa.unparse(rowsToDownload);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.setAttribute(
      "download",
      `${sessionStorage.getItem("partyCode")}_${sessionStorage.getItem("partyName")}_compare_file.csv`
    );
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleEdit = (row) => {
    const key = `${row["Batch Number"]}###${row["Expiry"]}###${row["MRP"]}`;
    setEditRowKey(key);
    setEditedRow({ ...row });
  };

  const handleSave = () => {
    if (!editRowKey) return;

    const updatedData = comparisonResult.map(row => {
      const key = `${row["Batch Number"]}###${row["Expiry"]}###${row["MRP"]}`;
      if (key === editRowKey) {
        return {
          ...editedRow,
          "Differences": (parseFloat(editedRow["Scanned Quantity"]) - parseFloat(editedRow["Customer Quantity"]))
        };
      }
      return row;
    });

    const reMergedData = mergeRows(updatedData);
    setComparisonResult(reMergedData);
    setEditRowKey(null);
    toast.success("Row updated and re-merged successfully!");
  };

  const handleDelete = (rowToDelete) => {
    const updatedData = comparisonResult.filter(row => row !== rowToDelete);
    setComparisonResult(updatedData);
    toast.info("Row deleted.");
  };

  const displayedResults = comparisonResult.filter(row =>
    downloadOption === "unmatched" ? parseFloat(row["Differences"]) !== 0 : true
  );

  return (
    <div className="container mt-4">
      {/* Party Selection */}
      <div className="row justify-content-center mb-4">
        <div className="col-md-8">
          <label className="form-label fw-bold">Select Party</label>
          <div className="position-relative">
            <input
              type="text"
              className="form-control"
              placeholder={loadingParties ? "Loading parties..." : "Search party by name, code, or address..."}
              value={partySearchTerm}
              onChange={(e) => {
                setPartySearchTerm(e.target.value);
                setShowPartyDropdown(true);
              }}
              onFocus={() => setShowPartyDropdown(true)}
              disabled={loadingParties}
            />
            {showPartyDropdown && !loadingParties && filteredParties.length > 0 && (
              <div
                className="position-absolute w-100 bg-white border border-top-0 shadow-lg"
                style={{ zIndex: 1000, maxHeight: "200px", overflowY: "auto" }}
              >
                {filteredParties.map((party, index) => (
                  <div
                    key={party._id || index}
                    className="p-2 border-bottom cursor-pointer"
                    onClick={() => handlePartySelect(party)}
                    onMouseEnter={(e) => (e.target.style.backgroundColor = "#f8f9fa")}
                    onMouseLeave={(e) => (e.target.style.backgroundColor = "white")}
                  >
                    <strong>{party.code}</strong> - {party.name} -{" "}
                    <span className="text-muted">{party.address}</span>
                  </div>
                ))}
              </div>
            )}
          </div>

          {loadingParties && (
            <div className="mt-2 text-muted small">
              <div className="spinner-border spinner-border-sm me-2" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              Fetching parties... ({parties.length} loaded)
            </div>
          )}

          {selectedPartyDetails && selectedParty && (
            <div className="mt-2 p-2 bg-light rounded">
              <strong>Selected:</strong> {selectedPartyDetails.code} - {selectedPartyDetails.name} -{" "}
              {selectedPartyDetails.address}
            </div>
          )}
        </div>
      </div>

      {/* Backdrop for dropdown */}
      <div
        className="position-fixed w-100 h-100"
        style={{ top: 0, left: 0, zIndex: 999, display: showPartyDropdown ? "block" : "none" }}
        onClick={() => setShowPartyDropdown(false)}
      />

      <hr />

      {/* File Upload */}
      <div className="row justify-content-around mb-3">
        <div className="col-md-5">
          <label className="form-label fw-bold">Stockist Claim</label>
          <input
            type="file"
            multiple
            accept=".csv, .xlsx, application/pdf, image/*"
            className="form-control"
            onChange={(e) => setStockistFile((prev) => [...prev, ...Array.from(e.target.files)])}
            ref={stockistRef}
            disabled={!selectedParty}
          />
          {stockistFile.length > 0 && (
            <ul className="mt-2">
              {stockistFile.map((file, idx) => (
                <li key={idx} className="text-muted small">
                  {file.name}
                </li>
              ))}
            </ul>
          )}
          <div className="mt-3">
            <button className="btn btn-success" onClick={handleCompare} disabled={loading || !selectedParty || !token}>
              {loading ? "Comparing..." : "Compare"}
            </button>
          </div>
        </div>

        <div className="col-md-5">
          <label className="form-label fw-bold">CFA Scanned</label>
          <input
            type="file"
            accept=".csv"
            className="form-control"
            onChange={(e) => setCfaFile(e.target.files[0])}
            ref={cfaRef}
            disabled={!selectedParty}
          />
          {cfaFile && <div className="mt-2 text-muted small">{cfaFile.name}</div>}
        </div>
      </div>

      {comparisonResult.length > 0 && (
        <>
          <hr />
          <div className="mt-4">
            <h5>Download Options:</h5>
            <div className="mb-2">
              <div className="form-check form-check-inline">
                <input
                  className="form-check-input"
                  type="radio"
                  name="downloadOption"
                  id="downloadAll"
                  value="all"
                  checked={downloadOption === "all"}
                  onChange={() => setDownloadOption("all")}
                />
                <label className="form-check-label" htmlFor="downloadAll">
                  All Data
                </label>
              </div>
              <div className="form-check form-check-inline">
                <input
                  className="form-check-input"
                  type="radio"
                  name="downloadOption"
                  id="downloadUnmatched"
                  value="unmatched"
                  checked={downloadOption === "unmatched"}
                  onChange={() => setDownloadOption("unmatched")}
                />
                <label className="form-check-label" htmlFor="downloadUnmatched">
                  Discrepancy
                </label>
              </div>
            </div>
          </div>

          <div className="mt-4">
            <h5>Comparison Result:</h5>
            <div className="d-flex justify-content-between mb-2">
              <input
                type="text"
                className="form-control me-2"
                placeholder="Search..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                style={{ maxWidth: "300px" }}
              />
              <button className="btn btn-primary" onClick={handleDownloadCSV}>
                Download CSV
              </button>
            </div>

            <div style={{ maxHeight: "580px", overflowY: "auto" }}>
              <table className="table table-bordered table-hover" style={{ borderCollapse: "collapse" }}>
                <thead className="table-light" style={{ position: "sticky", top: 0, zIndex: 1 }}>
                  <tr>
                    <th>#</th>
                    <th>Product Name</th>
                    <th>Batch Number</th>
                    <th>Expiry</th>
                    <th>MRP</th>
                    <th>Customer Quantity</th>
                    <th>Scanned Quantity</th>
                    <th>Differences</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {displayedResults
                    .filter((row) =>
                      Object.values(row).some((val) => String(val).toLowerCase().includes(searchTerm.toLowerCase()))
                    )
                    .sort((a, b) => {
                      const nameA =
                        (skuMap[`${a["Batch Number"]}|${a["Expiry"]}|${a["MRP"] ?? 0}`] ||
                          a["Product Name"] ||
                          "").toLowerCase();
                      const nameB =
                        (skuMap[`${b["Batch Number"]}|${b["Expiry"]}|${b["MRP"] ?? 0}`] ||
                          b["Product Name"] ||
                          "").toLowerCase();

                      // Batch Number
                      const batchA = String(a["Batch Number"] || "").toLowerCase();
                      const batchB = String(b["Batch Number"] || "").toLowerCase();

                      // Expiry
                      const expiryA = String(a["Expiry"] || "").toLowerCase();
                      const expiryB = String(b["Expiry"] || "").toLowerCase();

                      // Sorting Product → Batch → Expiry
                      if (nameA !== nameB) return nameA.localeCompare(nameB);
                      if (batchA !== batchB) return batchA.localeCompare(batchB);
                      return expiryA.localeCompare(expiryB);
                    })
                    .map((row, idx) => {
                      const diff = parseFloat(row["Differences"] ?? 0);
                      let rowClass = "";
                      if (!isNaN(diff)) {
                        if (diff > 0) rowClass = "table-success";
                        else if (diff < 0) rowClass = "table-danger";
                      }
                      const key = `${String(row["Batch Number"] || "").trim()}|${String(row["Expiry"] || "").trim()}|${Number(
                        row["MRP"] ?? 0
                      )}`;
                      const productName = skuMap[key] || row["Product Name"] || "";
                      const isEditing = editRowKey === `${row["Batch Number"]}###${row["Expiry"]}###${row["MRP"]}`;

                      return (
                        <tr key={idx} className={rowClass}>
                          <td>{idx + 1}</td>
                          <td>{productName}</td>
                          <td>
                            {isEditing ? (
                              <input
                                type="text"
                                className="form-control"
                                value={editedRow["Batch Number"]}
                                onChange={(e) => setEditedRow({ ...editedRow, "Batch Number": e.target.value })}
                                ref={editInputRef}
                              />
                            ) : row["Batch Number"]}
                          </td>
                          <td>
                            {isEditing ? (
                              <input
                                type="text"
                                className="form-control"
                                value={editedRow["Expiry"]}
                                onChange={(e) => setEditedRow({ ...editedRow, "Expiry": e.target.value })}
                              />
                            ) : row["Expiry"]}
                          </td>
                          <td>
                            {isEditing ? (
                              <input
                                type="number"
                                className="form-control"
                                value={editedRow["MRP"]}
                                onChange={(e) => setEditedRow({ ...editedRow, "MRP": e.target.value })}
                              />
                            ) : row["MRP"]}
                          </td>
                          <td>
                            {isEditing ? (
                              <input
                                type="number"
                                className="form-control"
                                value={editedRow["Customer Quantity"]}
                                onChange={(e) => setEditedRow({ ...editedRow, "Customer Quantity": e.target.value })}
                              />
                            ) : row["Customer Quantity"]}
                          </td>
                          <td>
                            {isEditing ? (
                              <input
                                type="number"
                                className="form-control"
                                value={editedRow["Scanned Quantity"]}
                                onChange={(e) => setEditedRow({ ...editedRow, "Scanned Quantity": e.target.value })}
                              />
                            ) : row["Scanned Quantity"]}
                          </td>
                          <td>{row["Differences"]}</td>
                          <td>
                            {isEditing ? (
                              <button className="btn btn-sm btn-success me-1" onClick={handleSave}>Save</button>
                            ) : (
                              <button className="btn btn-sm btn-primary me-1" onClick={() => handleEdit(row)}>Edit</button>
                            )}
                            <button className="btn btn-sm btn-danger" onClick={() => handleDelete(row)}>Delete</button>
                          </td>
                        </tr>
                      );
                    })}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {!token && (
        <div className="alert alert-warning" role="alert">
          Authentication token is missing. Please log in to use this feature.
        </div>
      )}
    </div>
  );
};

export default CompareFiles;